<div class="labor-tracking-container">
  <!-- Header -->
  <div class="header-section">
    <div class="header-top">
      <h2>Project Labor Tracking</h2>
      <button mat-raised-button color="primary" (click)="showAddLaborForm()" *ngIf="!showAddForm">
        <mat-icon>add</mat-icon>
        Add Labor
      </button>
    </div>
    <div class="summary-cards" *ngIf="laborEntries.length > 0">
      <div class="summary-card total-cost">
        <div class="card-content">
          <h3>{{ getTotalProjectLabor().totalCost | currency }}</h3>
          <p>Total Labor Cost</p>
        </div>
      </div>
      <div class="summary-card billed-amount">
        <div class="card-content">
          <h3>{{ getTotalProjectLabor().billedAmount | currency }}</h3>
          <p>Total Billed</p>
        </div>
      </div>
      <div class="summary-card gross-profit">
        <div class="card-content">
          <h3>{{ getTotalProjectLabor().grossProfit | currency }}</h3>
          <p>Gross Profit</p>
        </div>
      </div>
      <div class="summary-card total-hours">
        <div class="card-content">
          <h3>{{ getTotalProjectLabor().totalHours }}</h3>
          <p>Total Hours</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Add/Edit Labor Entry Form -->
  <mat-card class="labor-form-card" *ngIf="showAddForm">
    <mat-card-header>
      <mat-card-title>
        {{ editingEntry ? 'Edit Labor' : 'Add Labor' }}
      </mat-card-title>
    </mat-card-header>
    
    <mat-card-content>
      <form [formGroup]="laborForm" (ngSubmit)="onSubmit()">
        
        <!-- Worker Info Row -->
        <div class="form-row">
          <mat-form-field class="form-field">
            <mat-label>Worker Name</mat-label>
            <input matInput formControlName="workerName" required
                   [matAutocomplete]="workerAuto"
                   placeholder="Type or select a sub-contractor">
            <mat-autocomplete #workerAuto="matAutocomplete"
                              (optionSelected)="onContractorSelect($event.option.value)">
              <mat-option *ngFor="let c of filteredContractors" [value]="c.name">
                {{ c.name }}<span *ngIf="c.jobTitle"> · {{ c.jobTitle }}</span>
              </mat-option>
            </mat-autocomplete>
            <mat-error *ngIf="laborForm.get('workerName')?.hasError('required')">
              Worker name is required
            </mat-error>
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>Labor Type</mat-label>
            <mat-select formControlName="laborType" required>
              <mat-option *ngFor="let type of laborTypes" [value]="type">
                {{ type }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>Trade</mat-label>
            <mat-select formControlName="trade" required>
              <mat-option *ngFor="let trade of tradeTypes" [value]="trade">
                {{ trade }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Date and Time Row -->
        <div class="form-row">
          <mat-form-field class="form-field">
            <mat-label>Work Date</mat-label>
            <input matInput type="date" formControlName="workDate" required>
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>Start Time</mat-label>
            <input matInput type="time" formControlName="startTime">
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>End Time</mat-label>
            <input matInput type="time" formControlName="endTime">
          </mat-form-field>
        </div>

        <!-- Rate Basis Toggle -->
        <div class="form-row">
          <div class="checkbox-field">
            <mat-checkbox formControlName="isFlatRate">
              Flat Rate (lump sum for the whole job)
            </mat-checkbox>
          </div>
        </div>

        <!-- Hours Row -->
        <div class="form-row" *ngIf="!laborForm.get('isFlatRate')?.value">
          <mat-form-field class="form-field">
            <mat-label>Regular Hours</mat-label>
            <input matInput type="number" formControlName="regularHours" step="0.25" min="0" max="24">
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>Overtime Hours</mat-label>
            <input matInput type="number" formControlName="overtimeHours" step="0.25" min="0" max="24">
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>Double Time Hours</mat-label>
            <input matInput type="number" formControlName="doubleTimeHours" step="0.25" min="0" max="24">
          </mat-form-field>
        </div>

        <!-- Pay Rates Row -->
        <div class="form-row" *ngIf="!laborForm.get('isFlatRate')?.value">
          <mat-form-field class="form-field">
            <mat-label>Regular Rate ($/hr)</mat-label>
            <input matInput type="number" formControlName="regularRate" 
                   (blur)="onRegularRateChange()" step="0.01" min="0">
            <span matPrefix>$</span>
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>Overtime Rate ($/hr)</mat-label>
            <input matInput type="number" formControlName="overtimeRate" step="0.01" min="0">
            <span matPrefix>$</span>
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>Double Time Rate ($/hr)</mat-label>
            <input matInput type="number" formControlName="doubleTimeRate" step="0.01" min="0">
            <span matPrefix>$</span>
          </mat-form-field>
        </div>

        <!-- Flat Rate Row -->
        <div class="form-row" *ngIf="laborForm.get('isFlatRate')?.value">
          <mat-form-field class="form-field">
            <mat-label>Flat Rate &mdash; Contractor Cost ($)</mat-label>
            <input matInput type="text" inputmode="decimal" appCurrencyInput formControlName="flatRateAmount">
            <span matPrefix>$&nbsp;</span>
          </mat-form-field>

          <mat-form-field class="form-field">
            <mat-label>Flat Rate &mdash; Client Bill ($)</mat-label>
            <input matInput type="text" inputmode="decimal" appCurrencyInput formControlName="flatBillAmount">
            <span matPrefix>$&nbsp;</span>
          </mat-form-field>
        </div>

        <!-- Burden and Billing Row -->
        <div class="form-row">
          <mat-form-field class="form-field" *ngIf="!laborForm.get('isFlatRate')?.value">
            <mat-label>Burden Percentage (%)</mat-label>
            <input matInput type="number" formControlName="burdenPercentage" step="1" min="0" max="100">
            <span matSuffix>%</span>
          </mat-form-field>

          <mat-form-field class="form-field" *ngIf="!laborForm.get('isFlatRate')?.value">
            <mat-label>Billing Rate ($/hr)</mat-label>
            <input matInput type="number" formControlName="billingRate" step="0.01" min="0">
            <span matPrefix>$</span>
          </mat-form-field>

          <div class="checkbox-field">
            <mat-checkbox formControlName="isPrevailingWageJob">
              Prevailing Wage Job
            </mat-checkbox>
          </div>
          <div class="checkbox-field">
            <mat-checkbox formControlName="isPaid">
              Mark as Paid
            </mat-checkbox>
          </div>
        </div>

        <!-- Description and Task Row -->
        <div class="form-row">
          <mat-form-field class="form-field full-width">
            <mat-label>Phase/Task</mat-label>
            <input matInput formControlName="phaseOrTask" placeholder="e.g., Framing, Electrical, Cleanup">
          </mat-form-field>

          <mat-form-field class="form-field full-width">
            <mat-label>Description</mat-label>
            <textarea matInput formControlName="description" rows="2" 
                      placeholder="Brief description of work performed"></textarea>
          </mat-form-field>
        </div>

        <!-- Real-time Calculations -->
        <div class="calculations-section">
          <h4>Calculated Values</h4>
          <div class="calc-grid">
            <div class="calc-item">
              <label>Base Pay:</label>
              <span>{{ getCalculatedValues().basePay | currency }}</span>
            </div>
            <div class="calc-item">
              <label>Burden Amount:</label>
              <span>{{ getCalculatedValues().burdenAmount | currency }}</span>
            </div>
            <div class="calc-item">
              <label>Total Cost:</label>
              <span class="highlight">{{ getCalculatedValues().totalCost | currency }}</span>
            </div>
            <div class="calc-item">
              <label>Billed Amount:</label>
              <span>{{ getCalculatedValues().billedAmount | currency }}</span>
            </div>
            <div class="calc-item">
              <label>Gross Profit:</label>
              <span [class.profit]="getCalculatedValues().grossProfit >= 0" 
                    [class.loss]="getCalculatedValues().grossProfit < 0">
                {{ getCalculatedValues().grossProfit | currency }}
              </span>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <button mat-raised-button color="primary" type="submit" [disabled]="laborForm.invalid">
            <mat-icon>save</mat-icon>
            {{ editingEntry ? 'Update Entry' : 'Add Entry' }}
          </button>
          
          <button mat-button type="button" (click)="cancelAddForm()">
            <mat-icon>cancel</mat-icon>
            Cancel
          </button>
        </div>
      </form>
    </mat-card-content>
  </mat-card>

  <!-- Labor Entries Table -->
  <mat-card class="table-card">
    <mat-card-header>
      <mat-card-title>Labor Entries</mat-card-title>
    </mat-card-header>
    
    <mat-card-content>
      <div *ngIf="isLoading" class="loading-container">
        <mat-spinner diameter="50"></mat-spinner>
        <p>Loading labor entries...</p>
      </div>

      <div *ngIf="!isLoading && laborEntries.length === 0" class="no-data">
        <mat-icon>work_off</mat-icon>
        <p>No labor entries yet. Add the first entry above.</p>
      </div>

      <div *ngIf="!isLoading && laborEntries.length > 0" class="table-container">
        <table mat-table [dataSource]="dataSource" class="labor-table">
          
          <!-- Work Date Column -->
          <ng-container matColumnDef="workDate">
            <th mat-header-cell *matHeaderCellDef>Date</th>
            <td mat-cell *matCellDef="let entry">{{ entry.workDate | date:'shortDate' }}</td>
          </ng-container>

          <!-- Worker Name Column -->
          <ng-container matColumnDef="workerName">
            <th mat-header-cell *matHeaderCellDef>Worker</th>
            <td mat-cell *matCellDef="let entry">
              <div class="worker-cell">
                <div class="worker-avatar">{{ entry.workerName?.[0]?.toUpperCase() }}</div>
                <div class="worker-info">
                  <strong>{{ entry.workerName }}</strong>
                  <small>{{ entry.trade }}</small>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Trade Column -->
          <ng-container matColumnDef="trade">
            <th mat-header-cell *matHeaderCellDef>Trade</th>
            <td mat-cell *matCellDef="let entry">
              <span class="trade-badge" [attr.data-trade]="entry.trade">
                {{ entry.trade }}
              </span>
            </td>
          </ng-container>

          <!-- Labor Type Column -->
          <ng-container matColumnDef="laborType">
            <th mat-header-cell *matHeaderCellDef>Type</th>
            <td mat-cell *matCellDef="let entry">{{ entry.laborType }}</td>
          </ng-container>

          <!-- Regular Hours Column -->
          <ng-container matColumnDef="regularHours">
            <th mat-header-cell *matHeaderCellDef>Regular Hrs</th>
            <td mat-cell *matCellDef="let entry">{{ entry.regularHours }}</td>
          </ng-container>

          <!-- Overtime Hours Column -->
          <ng-container matColumnDef="overtimeHours">
            <th mat-header-cell *matHeaderCellDef>OT Hrs</th>
            <td mat-cell *matCellDef="let entry">{{ entry.overtimeHours }}</td>
          </ng-container>

          <!-- Total Cost Column -->
          <ng-container matColumnDef="totalCost">
            <th mat-header-cell *matHeaderCellDef>Total Cost</th>
            <td mat-cell *matCellDef="let entry">
              <strong>{{ entry.totalCost | currency }}</strong>
            </td>
          </ng-container>

          <!-- Billed Amount Column -->
          <ng-container matColumnDef="billedAmount">
            <th mat-header-cell *matHeaderCellDef>Billed</th>
            <td mat-cell *matCellDef="let entry">{{ entry.billedAmount | currency }}</td>
          </ng-container>

          <!-- Gross Profit Column -->
          <ng-container matColumnDef="grossProfit">
            <th mat-header-cell *matHeaderCellDef>Profit</th>
            <td mat-cell *matCellDef="let entry">
              <span [class.profit]="entry.grossProfit >= 0" [class.loss]="entry.grossProfit < 0">
                {{ entry.grossProfit | currency }}
              </span>
            </td>
          </ng-container>

          <!-- Paid Status Column -->
          <ng-container matColumnDef="isPaid">
            <th mat-header-cell *matHeaderCellDef>Paid</th>
            <td mat-cell *matCellDef="let entry">
              <button mat-icon-button (click)="togglePaid(entry)"
                      [matTooltip]="entry.isPaid ? 'Paid · ' + (entry.paidDate | date:'shortDate') : 'Mark as paid'"
                      [color]="entry.isPaid ? 'primary' : ''">
                <mat-icon>{{ entry.isPaid ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
              </button>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let entry">
              <div class="action-buttons">
                <button mat-icon-button (click)="editEntry(entry)" matTooltip="Edit Entry">
                  <mat-icon>edit</mat-icon>
                </button>
                <button mat-icon-button color="warn" (click)="deleteEntry(entry)" matTooltip="Delete Entry">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>
      </div>
    </mat-card-content>
  </mat-card>
</div>
